import React, { useCallback, useEffect, useRef, useState } from 'react'

export const useScript = (
    properties: { [string]: any },
    parentNodeId?: string
) => {
    useEffect(() => {
        const script = document.createElement('script')
        for (const [key, value] of Object.entries(properties)) {
            //$FlowFixMe
            script[key] = value
        }

        const parent = !!parentNodeId && document.getElementById(parentNodeId)
        parent
            ? parent.appendChild(script)
            : document.body && document.body.appendChild(script)
        return () => {
            parent
                ? parent.removeChild(script)
                : document.body && document.body.removeChild(script)
        }
    }, [])
}

const handleScriptAttributes = (script, otherProps) => {
    for (const [attr, value] of Object.entries(otherProps)) {
        script.setAttribute(attr, value)
    }
}

type Props = {
    src?: string,
    otherProps?: { [string]: any },
    id?: string,
    innerText?: string,
    innerHTML?: string,
    appendTo?: string,
    appendToId?: string
}

export const useAsyncScript = (props: Props) => {
    const {
        src,
        otherProps,
        id,
        innerText,
        innerHTML,
        appendTo = 'head',
        appendToId
    } = props
    const isLoading = useRef(false)
    const [state, setState] = useState({
        ready: false,
        error: null
    })
    const handleOnLoad = useCallback(() => {
        setState(() => ({ ready: true, error: null }))
    }, [isLoading?.current])

    const handleOnError = useCallback(error => {
        setState(() => ({ ready: false, error }))
    }, [])

    const canRunEffect =
        (typeof src === 'string' && src?.length > 0) ||
        (typeof innerText === 'string' && innerText?.length > 0) ||
        (typeof innerHTML === 'string' && innerHTML?.length > 0)

    useEffect(() => {
        if (canRunEffect) {
            const parent = !!appendToId && document.getElementById(appendToId)
            const script = global.document.createElement('script')
            try {
                if (innerText && !innerHTML && !src) {
                    script.innerText = innerText.toString()
                }

                if (innerHTML && !innerText && !src) {
                    script.innerHTML = innerHTML.toString()
                }

                if (src && !innerText && !innerHTML) {
                    script.src = src.toString()
                }

                script.id = id

                if (otherProps) {
                    handleScriptAttributes(script, otherProps)
                }

                script.onload = () => handleOnLoad()

                script.onerror = handleOnError

                parent
                    ? parent.appendChild(script)
                    : global.document[appendTo].appendChild(script)

                if ((innerText || innerHTML) && !src) {
                    handleOnLoad()
                }
            } catch (error) {
                handleOnError(error)
            }
            return () => {
                parent
                    ? parent.removeChild(script)
                    : global.document[appendTo] &&
                      global.document[appendTo].removeChild(script)
            }
        }
    }, [
        otherProps,
        id,
        appendTo,
        handleOnLoad,
        handleOnError,
        canRunEffect,
        innerText,
        src,
        isLoading?.current
    ])

    return state
}
